import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Chip,
    Collapse,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import { APPLICATION_CONTEXT_LABELS } from '../context/applicationContextPresentation';
import { formatUserFactorModel } from '../utils/factorDataOptions';

const text = (value, fallback = 'Unavailable') => {
    const normalized = value == null ? '' : String(value).trim();
    return normalized || fallback;
};

const joined = (...values) => values
    .map((value) => text(value, ''))
    .filter(Boolean)
    .join(' · ');

const normalizedValue = (value) => String(value ?? '').trim().toLowerCase();
const valuesMatch = (left, right) => normalizedValue(left) === normalizedValue(right);

const STATUS_PRESENTATION = Object.freeze({
    current: ['In sync', 'success'],
    update_available: ['Newer inputs available', 'warning'],
    stale: ['Uses saved inputs', 'warning'],
    modified: ['Saved result modified', 'warning'],
    saved_context_available: ['Saved result available', 'success'],
    restoration_required: ['Needs attention', 'error'],
    not_ready: ['Not ready', 'warning'],
    unavailable: ['Needs attention', 'warning'],
    unknown: ['Comparison unavailable', 'info'],
    checking: ['Checking', 'info'],
    loading: ['Loading', 'info'],
    empty: ['None saved', 'default'],
    available: ['Available', 'success'],
    latest_context_unavailable: ['Needs attention', 'error'],
    read_error: ['Needs attention', 'error'],
    authority_inconsistent: ['Needs attention', 'error'],
});
const ATTENTION_STATUSES = new Set([
    'update_available', 'stale', 'modified', 'restoration_required', 'not_ready',
    'unavailable', 'unknown', 'latest_context_unavailable', 'read_error',
    'authority_inconsistent',
]);
const formatStatus = (status) => {
    const [label, color] = STATUS_PRESENTATION[status] ?? [
        status ? text(status).replaceAll('_', ' ') : 'Checking',
        'default',
    ];
    return { label, color };
};
const needsAttention = (status) => ATTENTION_STATUSES.has(status);

const factorContextSummary = (context = {}) => joined(
    context.factorModel ? formatUserFactorModel(context.factorModel) : null,
    context.factorDataModel,
    context.factorSnapshotDate,
) || 'Unavailable';

function comparisonRow(key, label, usingValue, availableValue, meaning, matches) {
    return {
        key,
        label,
        usingValue: text(usingValue),
        availableValue: text(availableValue),
        meaning,
        matches: matches ?? valuesMatch(usingValue, availableValue),
    };
}

function factorDataDetails(factor) {
    const using = factor.usingContext ?? {};
    const available = factor.availableContext ?? using;
    return {
        explanation: factor.detailText,
        usingLabel: 'Using now',
        availableLabel: 'Available on server',
        rows: [
            comparisonRow(
                'provider',
                'Factor provider',
                using.factorModel ? formatUserFactorModel(using.factorModel) : null,
                available.factorModel ? formatUserFactorModel(available.factorModel) : null,
                'Selects the source of portfolio risk data.',
            ),
            comparisonRow(
                'model',
                'Factor model',
                using.factorDataModel,
                available.factorDataModel,
                'Defines the risk factors available to calculations.',
            ),
            comparisonRow(
                'snapshot',
                'Factor data date',
                using.factorSnapshotDate,
                available.factorSnapshotDate,
                'Identifies the market date represented by the factor data.',
            ),
            ...((using.factorRelease != null || available.factorRelease != null) ? [comparisonRow(
                'release',
                'Factor release',
                using.factorRelease,
                available.factorRelease,
                'Identifies the published factor dataset.',
            )] : []),
        ],
    };
}

function positionSizingDetails(positionSizing) {
    const summary = positionSizing.summary ?? {};
    return {
        explanation: summary.detailText,
        usingLabel: 'Saved Position Sizing',
        availableLabel: 'Current parent',
        rows: (summary.contextComparisonRows ?? []).map((row) => ({
            ...row,
            usingValue: row.savedValue,
            availableValue: row.currentValue,
        })),
        technicalDetails: positionSizing.contextDigest
            ? [{ label: 'Published context digest', value: positionSizing.contextDigest }]
            : [],
    };
}

function optimizedPortfolioDetails(portfolio) {
    const presentation = portfolio.contextPresentation ?? {};
    const parentLabel = (
        portfolio.parent === 'pos_sizing'
        || presentation.parentContextId === 'position_sizing'
    )
        ? APPLICATION_CONTEXT_LABELS.position_sizing
        : 'Actual Portfolio';
    const comparisonState = presentation.updateAvailable
        ? 'Newer consumed inputs available'
        : presentation.comparisonUnknown
            ? 'Comparison unavailable'
            : 'No tracked update';
    const materializationState = presentation.materializationState
        ?? portfolio.materializationState;
    const savedState = materializationState === 'modified'
        ? 'Edited since optimization'
        : 'Original saved result';
    const reasons = Array.isArray(presentation.reasons)
        ? presentation.reasons
        : portfolio.staleReasons;
    const reasonText = Array.isArray(reasons)
        ? reasons.map((reason) => text(reason).replaceAll('_', ' ')).join(', ')
        : '';
    const explanation = [
        presentation.historicalAvailable === false
            ? 'The saved historical optimizer run is unavailable.'
            : 'The saved historical optimizer run remains available.',
        materializationState === 'modified'
            ? 'Its saved weights were edited after optimization.'
            : null,
        presentation.updateAvailable
            ? `Newer consumed inputs are available${reasonText ? ` because ${reasonText}` : ''}.`
            : null,
        presentation.restorationRequired
            ? 'Missing saved securities must be restored before using current Watchlist inputs.'
            : null,
        presentation.comparisonUnknown
            ? 'Comparison with current inputs is unavailable; this does not invalidate the saved run.'
            : null,
        presentation.comparisonIncomplete && !presentation.comparisonUnknown
            ? 'Some current inputs could not be compared; known changes are still shown.'
            : null,
    ].filter(Boolean).join(' ');
    return {
        explanation,
        usingLabel: 'Saved optimization',
        availableLabel: 'Current app',
        rows: [
            comparisonRow(
                'historical-run',
                'Historical saved run',
                presentation.historicalAvailable === false ? 'Unavailable' : 'Available',
                parentLabel,
                'Preserves the immutable optimizer receipt independently of current inputs.',
                presentation.historicalAvailable !== false,
            ),
            comparisonRow(
                'comparison',
                'Current input comparison',
                parentLabel,
                comparisonState,
                'Compares only declared current inputs consumed by the optimizer run.',
                !presentation.updateAvailable && !presentation.comparisonUnknown,
            ),
            ...(presentation.comparisonIncomplete && !presentation.comparisonUnknown ? [
                comparisonRow(
                    'comparison-coverage',
                    'Comparison coverage',
                    'Known inputs compared',
                    'Some inputs unavailable',
                    'Unavailable evidence does not hide changes proved by available evidence.',
                    false,
                ),
            ] : []),
            comparisonRow(
                'result',
                'Saved result',
                savedState,
                materializationState === 'modified'
                    ? 'Saved weights were edited'
                    : 'Result is intact',
                'Shows whether saved portfolio weights changed after the optimizer run.',
                materializationState !== 'modified',
            ),
            comparisonRow(
                'watchlist-compatibility',
                'Current Watchlist',
                'Saved security scope',
                presentation.restorationRequired ? 'Restoration required' : 'Compatible',
                'Restoration affects current-workspace use, not historical run validity.',
                !presentation.restorationRequired,
            ),
        ],
        technicalDetails: [
            portfolio.factorModel && {
                label: 'Saved factor inputs',
                value: joined(
                    formatUserFactorModel(portfolio.factorModel),
                    portfolio.factorDataModel,
                    portfolio.factorSnapshotDate,
                ),
            },
            portfolio.lastRefreshedAt && {
                label: 'Last refreshed',
                value: portfolio.lastRefreshedAt,
            },
        ].filter(Boolean),
    };
}

function statusSupportedByComparison(status, details) {
    return { status: status || 'unknown', details };
}

function optimizedPortfolioStatus(portfolio) {
    const presentation = portfolio.contextPresentation ?? {};
    if (presentation.historicalAvailable === false) return 'unavailable';
    if (presentation.restorationRequired) return 'restoration_required';
    if ((presentation.materializationState ?? portfolio.materializationState) === 'modified') {
        return 'modified';
    }
    if (presentation.updateAvailable) return 'update_available';
    if (presentation.comparisonUnknown) return 'unknown';
    return portfolio.status || 'saved_context_available';
}

function ComparisonDetails({ details, showAll, onShowAllChange }) {
    const rows = Array.isArray(details?.rows) ? details.rows : [];
    const changedRows = rows.filter((row) => row.matches === false);
    const visibleRows = showAll || changedRows.length === 0 ? rows : changedRows;
    return (
        <Stack spacing={1.25} sx={{ py: 1.5, px: { xs: 1, md: 2 } }}>
            {details?.explanation ? (
                <Typography variant="body2" color="text.secondary">
                    {details.explanation}
                </Typography>
            ) : null}
            {rows.length > 0 ? (
                <TableContainer
                    sx={{
                        border: (theme) => `1px solid ${alpha(theme.palette.divider, 0.9)}`,
                        borderRadius: 1.5,
                    }}
                >
                    <Table size="small" aria-label="Context input comparison">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ width: '21%' }}>Input</TableCell>
                                <TableCell sx={{ width: '24%' }}>{details.usingLabel}</TableCell>
                                <TableCell sx={{ width: '24%' }}>{details.availableLabel}</TableCell>
                                <TableCell>What it affects</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {visibleRows.map((row) => {
                                const highlight = row.matches === false
                                    ? { bgcolor: (theme) => alpha(theme.palette.warning.main, 0.09) }
                                    : undefined;
                                return (
                                    <TableRow key={row.key} data-context-changed={row.matches === false ? 'true' : 'false'}>
                                        <TableCell sx={{ fontWeight: 650 }}>{row.label}</TableCell>
                                        <TableCell sx={highlight}>{text(row.usingValue)}</TableCell>
                                        <TableCell sx={highlight}>{text(row.availableValue)}</TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {row.meaning}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Detailed comparison data is not available yet.
                </Typography>
            )}
            {rows.length > changedRows.length && changedRows.length > 0 ? (
                <Button
                    size="small"
                    variant="text"
                    onClick={() => onShowAllChange(!showAll)}
                    sx={{ alignSelf: 'flex-start' }}
                >
                    {showAll ? 'Show changes only' : `Show all inputs (${rows.length})`}
                </Button>
            ) : null}
            {Array.isArray(details?.technicalDetails) && details.technicalDetails.length > 0 ? (
                <Box component="details">
                    <Typography component="summary" variant="caption" sx={{ cursor: 'pointer' }}>
                        Technical details
                    </Typography>
                    <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                        {details.technicalDetails.map((item) => (
                            <Box key={item.label} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {item.label}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ fontFamily: 'monospace', overflowWrap: 'anywhere' }}
                                >
                                    {item.value}
                                </Typography>
                            </Box>
                        ))}
                    </Stack>
                </Box>
            ) : null}
        </Stack>
    );
}

export default function DataContextLineageSettings({ snapshot = null, showHeader = true }) {
    const [expandedRows, setExpandedRows] = useState(() => new Set());
    const [showAllRows, setShowAllRows] = useState(() => new Set());
    const factor = snapshot?.factorData ?? {};
    const msi = snapshot?.masterSecurityIdentity ?? {};
    const positionSizing = snapshot?.positionSizing ?? {};
    const optimized = Array.isArray(snapshot?.optimizedPortfolios)
        ? snapshot.optimizedPortfolios
        : [];

    const rows = useMemo(() => {
        const factorAvailable = factorContextSummary(factor.availableContext ?? factor.usingContext);
        const posSummary = positionSizing.summary ?? {};
        const positionChangedCount = (posSummary.contextComparisonRows ?? [])
            .filter((row) => row.matches === false).length;
        const factorComparison = statusSupportedByComparison(
            factor.loading ? 'loading' : factor.status,
            factorDataDetails(factor),
        );
        const positionSizingComparison = statusSupportedByComparison(
            positionSizing.status,
            positionSizingDetails(positionSizing),
        );
        const baseRows = [
            {
                key: 'factor-data',
                name: APPLICATION_CONTEXT_LABELS.factor_data,
                relationship: 'Current / available data',
                usingNow: factorContextSummary(factor.usingContext),
                availableNow: factorComparison.status === 'update_available'
                    ? factorAvailable
                    : factorComparison.status === 'current'
                        ? 'Matches loaded context'
                        : factorAvailable,
                status: factorComparison.status,
                details: factorComparison.details,
            },
            {
                key: 'msi',
                name: APPLICATION_CONTEXT_LABELS.master_security_identity,
                relationship: 'Automatic maintenance',
                usingNow: 'Managed automatically',
                availableNow: 'No manual sync required',
                status: ['current', 'update_available'].includes(msi.status) ? 'current' : msi.status,
                details: null,
            },
            {
                key: 'position-sizing',
                name: APPLICATION_CONTEXT_LABELS.position_sizing,
                relationship: 'Watchlist + Actual',
                usingNow: positionSizing.availability === 'available'
                    ? 'Saved draft available'
                    : text(positionSizing.availability, 'Checking saved draft'),
                availableNow: positionSizingComparison.status === 'current'
                    ? 'Matches parent context'
                    : positionSizingComparison.status === 'stale' && positionChangedCount > 0
                        ? `${positionChangedCount} parent input${positionChangedCount === 1 ? '' : 's'} changed`
                        : positionSizingComparison.status === 'unknown'
                            ? 'Comparison unavailable'
                            : text(posSummary.freshnessLabel, 'Checking parent context'),
                status: positionSizingComparison.status,
                details: positionSizingComparison.details,
            },
        ];
        if (optimized.length === 0) {
            baseRows.push({
                key: 'optimized-empty',
                name: APPLICATION_CONTEXT_LABELS.optimized_portfolio,
                relationship: 'Actual or Position Sizing',
                usingNow: 'No saved portfolios',
                availableNow: 'Create one in Optimizer',
                status: 'empty',
                details: null,
            });
            return baseRows;
        }
        optimized.forEach((portfolio) => {
            const presentation = portfolio.contextPresentation ?? {};
            const parentLabel = (
                portfolio.parent === 'pos_sizing'
                || presentation.parentContextId === 'position_sizing'
            )
                ? 'Position Sizing'
                : 'Actual Portfolio';
            const optimizedComparison = statusSupportedByComparison(
                optimizedPortfolioStatus(portfolio),
                optimizedPortfolioDetails(portfolio),
            );
            baseRows.push({
                key: `optimized-${portfolio.id}`,
                name: text(portfolio.name, 'Saved optimized portfolio'),
                nameCaption: 'Saved optimized portfolio',
                relationship: parentLabel,
                usingNow: (presentation.materializationState
                    ?? portfolio.materializationState) === 'modified'
                    ? 'Saved result · edited'
                    : 'Saved result intact',
                availableNow: presentation.restorationRequired
                    ? 'Restore missing Watchlist securities'
                    : presentation.updateAvailable
                        ? 'Newer consumed inputs available'
                        : presentation.comparisonUnknown
                            ? 'Current comparison unavailable'
                            : 'No tracked update',
                status: optimizedComparison.status,
                details: optimizedComparison.details,
            });
        });
        return baseRows;
    }, [factor, msi, optimized, positionSizing]);

    const attentionKeys = useMemo(
        () => rows.filter((row) => needsAttention(row.status)).map((row) => row.key),
        [rows],
    );
    const attentionKey = attentionKeys.join('|');
    useEffect(() => {
        if (!attentionKey) return;
        setExpandedRows((current) => {
            const next = new Set(current);
            attentionKeys.forEach((key) => next.add(key));
            return next;
        });
    }, [attentionKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const toggleSetValue = (setter, key) => setter((current) => {
        const next = new Set(current);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
    });

    return (
        <Box sx={{ width: '100%', maxWidth: 1120, mx: 'auto', display: 'grid', gap: 2 }}>
            {showHeader && (
                <Box sx={{ display: 'grid', gap: 0.45 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        Workspace Data &amp; Sync
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Alphamizer preserves the exact inputs used by each workspace. “Using now” is
                        the saved or loaded state; “Available now” is the latest server version or
                        current parent state. Expand a row to see what differs and why it matters.
                    </Typography>
                </Box>
            )}

            {!snapshot ? (
                <Alert severity="info">Context status is still loading from the workspace.</Alert>
            ) : (
                <Alert severity={factor.loading ? 'info' : attentionKeys.length > 0 ? 'warning' : 'success'}>
                    {factor.loading
                        ? (factor.detailText || 'Updating factor data. Please wait while the update completes.')
                        : attentionKeys.length > 0
                        ? `${attentionKeys.length} workspace${attentionKeys.length === 1 ? '' : 's'} use saved inputs or need review.`
                        : 'All available workspaces are in sync with their current inputs.'}
                </Alert>
            )}

            <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2.2 }}>
                <Table size="small" aria-label="Workspace data and sync status">
                    <TableHead>
                        <TableRow>
                            <TableCell padding="checkbox" />
                            <TableCell sx={{ width: '22%' }}>Workspace</TableCell>
                            <TableCell sx={{ width: '18%' }}>Compared with</TableCell>
                            <TableCell sx={{ width: '23%' }}>Using now</TableCell>
                            <TableCell sx={{ width: '23%' }}>Available now</TableCell>
                            <TableCell align="right">Status</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => {
                            const expanded = expandedRows.has(row.key);
                            const status = formatStatus(row.status);
                            return (
                                <React.Fragment key={row.key}>
                                    <TableRow
                                        hover={Boolean(row.details)}
                                        sx={{
                                            '& > *': { borderBottom: expanded ? 'unset' : undefined },
                                            ...(needsAttention(row.status) ? {
                                                bgcolor: (theme) => alpha(theme.palette.warning.main, 0.035),
                                            } : {}),
                                        }}
                                    >
                                        <TableCell padding="checkbox">
                                            {row.details ? (
                                                <Tooltip title={expanded ? 'Hide context details' : 'Show context details'}>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={`${expanded ? 'Hide' : 'Show'} ${row.name} context details`}
                                                        aria-expanded={expanded}
                                                        onClick={() => toggleSetValue(setExpandedRows, row.key)}
                                                    >
                                                        {expanded
                                                            ? <KeyboardArrowDownRoundedIcon />
                                                            : <KeyboardArrowRightRoundedIcon />}
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" sx={{ fontWeight: 750 }}>
                                                {row.name}
                                            </Typography>
                                            {row.nameCaption ? (
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.nameCaption}
                                                </Typography>
                                            ) : null}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {row.relationship}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.usingNow}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">{row.availableNow}</Typography>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Chip
                                                size="small"
                                                variant="outlined"
                                                color={status.color}
                                                label={status.label}
                                            />
                                        </TableCell>
                                    </TableRow>
                                    {row.details ? (
                                        <TableRow>
                                            <TableCell colSpan={6} sx={{ py: 0, bgcolor: 'action.hover' }}>
                                                <Collapse in={expanded} timeout="auto" unmountOnExit>
                                                    <ComparisonDetails
                                                        details={row.details}
                                                        showAll={showAllRows.has(row.key)}
                                                        onShowAllChange={() => toggleSetValue(setShowAllRows, row.key)}
                                                    />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    ) : null}
                                </React.Fragment>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>

            <Typography variant="caption" color="text.secondary">
                In sync means the saved inputs match their comparison source. Uses saved inputs
                means the workspace is preserved and usable, but its parent has changed. Refresh
                and Sync actions remain in the screens that own those workflows.
            </Typography>
        </Box>
    );
}
